import {
  Component,
  OnInit,
  inject,
  DestroyRef,
  ChangeDetectionStrategy,
  signal,
  computed,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { ActivatedRoute } from "@angular/router";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { catchError, of, Observable, first } from "rxjs";

import { ProfileShellComponent } from "../shared/components/profile-shell/profile-shell.component";
import { ProfileTab } from "../shared/components/profile-shell/profile-tab.interface";
import { UserService } from "../shared/services/user.service";
import { CurrentUserStore } from "../shared/stores/current-user.store";
import { AuthenticationService } from "../shared/services/authentication.service";
import { ModalService } from "../shared/services/modal.service";
import { RoleTypes } from "../guards/roletypes";
import { empty } from "../functions";

import { StudentProfile } from "../api/models/student-profile";
import { TeacherProfile } from "../api/models/teacher-profile";
import { SchoolClass } from "../api/models/school-class";
import { StudentProfileService } from "../api/services/student-profile.service";
import { TeacherProfileService } from "../api/services/teacher-profile.service";
import { MatchQuestionService } from "../api/services/match-question.service";

import { ModalStudentProfileAvatarComponent } from "../student/student-profile/modal-student-profile-avatar/modal-student-profile-avatar.component";
import {
  getStudentProfileTabs,
  getTeacherProfileTabs,
} from "./profile-tab-registry";

// Student tab components
import {
  StudentTopPanelComponent,
  StudentTopPanelActionsComponent,
} from "./tabs/student/student-top-panel.component";
import { StudentSidebarComponent } from "./tabs/student/student-sidebar.component";
import { StudentContactDetailsComponent } from "./tabs/student/student-contact-details.component";
import { StudentCvTabComponent } from "./tabs/student/student-cv-tab.component";
import { StudentMatchtestTabComponent } from "./tabs/student/student-matchtest-tab.component";
import { StudentMatchesCompanyTabComponent } from "./tabs/student/student-matches-company-tab.component";
import { StudentMatchesJobTabComponent } from "./tabs/student/student-matches-job-tab.component";
import { StudentDnaTabComponent } from "./tabs/student/student-dna-tab.component";
import { StudentExperiencedayTabComponent } from "./tabs/student/student-experienceday-tab.component";
import { StudentOproepenTabComponent } from "./tabs/student/student-oproepen-tab.component";

// Teacher tab components
import { TeacherTopPanelComponent } from "./tabs/teacher/teacher-top-panel.component";
import { TeacherContactDetailsComponent } from "./tabs/teacher/teacher-contact-details.component";
import { TeacherProfileTabComponent } from "./tabs/teacher/teacher-profile-tab.component";
import { TeacherStudentsTabComponent } from "./tabs/teacher/teacher-students-tab.component";

@Component({
  selector: "app-profile",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ProfileShellComponent,
    // Student
    StudentTopPanelComponent,
    StudentTopPanelActionsComponent,
    StudentSidebarComponent,
    StudentContactDetailsComponent,
    StudentCvTabComponent,
    StudentMatchtestTabComponent,
    StudentMatchesCompanyTabComponent,
    StudentMatchesJobTabComponent,
    StudentDnaTabComponent,
    StudentExperiencedayTabComponent,
    StudentOproepenTabComponent,
    // Teacher
    TeacherTopPanelComponent,
    TeacherContactDetailsComponent,
    TeacherProfileTabComponent,
    TeacherStudentsTabComponent,
  ],
  templateUrl: "./profile.component.html",
})
export class ProfileComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly userService = inject(UserService);
  private readonly currentUserStore = inject(CurrentUserStore);
  private readonly authService = inject(AuthenticationService);
  private readonly modal = inject(ModalService);
  private readonly route = inject(ActivatedRoute);
  private readonly studentProfileService = inject(StudentProfileService);
  private readonly teacherProfileService = inject(TeacherProfileService);
  private readonly matchQuestionService = inject(MatchQuestionService);

  // Role detection
  readonly role = signal<string>("");

  // Shared state
  readonly isLoading = signal(false);
  readonly account = toSignal(this.authService.profile$, {
    initialValue: null,
  });
  readonly activeTab = signal<string>("");

  // Student state
  readonly studentProfile = signal<StudentProfile | null>(null);
  readonly matchtest = signal<any[]>([]);
  readonly profileRemainingSubjects = signal<any[]>([
    {
      validated: false,
      value: "areaOfInterests",
      label: "Extend your profile with field of interest",
      section: "sectionareaofinterests",
    },
    {
      validated: false,
      value: "workExperiences",
      label: "Extend your profile with work experiences",
      section: "sectionworkexperiences",
    },
    {
      validated: false,
      value: "educations",
      label: "Extend your profile with educations",
      section: "sectioneducations",
    },
    {
      validated: false,
      value: "languageProficiencies",
      label: "Extend your profile with languages you comprehend",
      section: "sectionlanguages",
    },
    {
      validated: false,
      value: "foreignExperiences",
      label: "Extend your profile with foreign experiences",
      section: "sectionforeignexperiences",
    },
    {
      validated: false,
      value: "interests",
      label: "Extend your profile with your hobbys",
      section: "sectioninterests",
    },
    {
      validated: false,
      value: "studentProfileCvs",
      label: "Upload your resume",
      section: "sectionresume",
    },
  ]);

  // Teacher state
  readonly teacherProfile = signal<TeacherProfile | null>(null);
  readonly schoolClasses = signal<SchoolClass[]>([]);

  // Computed
  readonly shellProfileCity = computed(() => {
    if (this.role() === "Student") {
      return this.studentProfile()?.homeAddress?.city || "";
    }
    return this.teacherProfile()?.address?.city || "";
  });

  // Name derived from the freshly-loaded student profile so the header renders
  // immediately on a newly created profile (the account name lags behind). Empty for
  // other roles, letting the shell fall back to the account-derived name.
  readonly shellProfileName = computed(() => {
    if (this.role() === "Student") {
      const p = this.studentProfile();
      if (!p) return "";
      return [p.firstName, p.infix, p.lastName].filter(Boolean).join(" ");
    }
    return "";
  });

  readonly profileTabs = computed<ProfileTab[]>(() => {
    if (this.role() === "Student") {
      return getStudentProfileTabs(
        {
          onMatchCompanyActivate: () => {},
          onMatchJobpostingActivate: () => {},
          onDnaActivate: () => {},
        },
        { capabilities: this.currentUserStore.capabilities }
      );
    }
    if (this.role() === "Teacher") {
      return getTeacherProfileTabs();
    }
    return [];
  });

  ngOnInit(): void {
    // Detect role
    if (this.userService.currentUserHasRole(RoleTypes.Student)) {
      this.role.set("Student");
      this.activeTab.set("cv");
      this.loadStudentProfile(false);
    } else if (this.userService.currentUserHasRole(RoleTypes.Teacher)) {
      this.role.set("Teacher");
      this.activeTab.set("profile");
      this.loadTeacherProfile();
    }

    // Handle query param tab selection
    this.route.queryParams
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        if (params["tab"]) {
          this.activeTab.set(params["tab"]);
        }
      });
  }

  reloadProfile(): void {
    if (this.role() === "Student") {
      this.loadStudentProfile(true);
    } else if (this.role() === "Teacher") {
      this.loadTeacherProfile();
    }
  }

  editAvatar(): void {
    this.authService.profile$.pipe(first()).subscribe((account) => {
      if (account !== null) {
        const data = Object.assign({}, account);
        this.modal
          .Modal(
            ModalStudentProfileAvatarComponent,
            "Profile image",
            "",
            data,
            [],
            { backdrop: "static", size: "lg" }
          )
          .then((result) => {
            if (result === true) this.reloadProfile();
          })
          .catch(() => {});
      }
    });
  }

  goToResumeSection(section: string): void {
    if (!section) return;
    this.activeTab.set("cv");
    setTimeout(() => {
      const element = document.getElementById(section);
      if (element) element.scrollIntoView(true);
    });
  }

  // ========== Student Profile Loading ==========

  private loadStudentProfile(refresh: boolean): void {
    if (!refresh) this.isLoading.set(true);

    this.studentProfileService
      .getCurrentStudentProfile()
      .pipe(
        catchError(this.handleError("getCurrentStudentProfile")),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((response) => {
        if (!refresh) this.isLoading.set(false);

        if (!empty(response)) {
          this.studentProfile.set(
            Object.assign({}, response as StudentProfile)
          );
          this.validateRemainingProfileSubjects();

          if (this.studentProfile()?.id) {
            this.loadMatchTest();
          }
        }
      });
  }

  private loadMatchTest(): void {
    if (!this.studentProfile()?.id) return;

    this.studentProfileService
      .getStudentProfileMatchTestItems({
        profileId: this.studentProfile()!.id!,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response) => {
        if (!empty(response)) {
          this.matchtest.set([...response]);
        } else {
          this.matchQuestionService
            .getMatchQuestions()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((resp) => {
              if (resp?.data) this.matchtest.set([...resp.data]);
            });
        }
        this.showPopupMatchtest();
      });
  }

  private validateRemainingProfileSubjects(): void {
    const entity = this.studentProfile();
    if (!entity) return;

    this.profileRemainingSubjects.update((subjects) =>
      subjects.map((subject) => {
        if (subject.hasOwnProperty("value")) {
          const key = subject["value"] as keyof StudentProfile;
          const value = entity[key];
          return {
            ...subject,
            validated: Array.isArray(value) && value.length > 0,
          };
        }
        return subject;
      })
    );
  }

  private showPopupMatchtest(): void {
    let show = true;
    if (this.matchtest().length > 0) {
      for (const q of this.matchtest()) {
        if (q["selectedAnswerIndex"] !== null) show = false;
      }
    } else {
      show = false;
    }

    if (!show) return;

    this.modal
      .Confirm(
        "Match test",
        "It seems you did not tried our match test yet. Answer our short survey to match with companies and jobs."
      )
      .then((result) => {
        if (result === true) this.activeTab.set("matchtest");
      })
      .catch(() => {});
  }

  // ========== Teacher Profile Loading ==========

  private loadTeacherProfile(): void {
    this.isLoading.set(true);

    this.teacherProfileService
      .getCurrentTeacherProfile()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (profile) => {
          this.teacherProfile.set(profile);
          this.isLoading.set(false);

          if (profile.id) {
            this.loadSchoolClasses(profile.id);
          }
        },
        error: (err) => {
          console.error("Failed to load teacher profile:", err);
          this.isLoading.set(false);
        },
      });
  }

  private loadSchoolClasses(profileId: string): void {
    this.teacherProfileService
      .getTeacherProfileSchoolClasses({ profileId })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (classes) => this.schoolClasses.set(classes || []),
        error: (err) => console.error("Failed to load school classes:", err),
      });
  }

  // ========== Error Handler ==========

  private handleError<T>(operation: string = "", result?: T) {
    return (_error: any): Observable<T> => {
      switch (operation) {
        case "getCurrentStudentProfile":
          break;
        default:
          this.modal.Alert("Error", "Something went wrong, please try again");
          break;
      }
      return of(result as T);
    };
  }
}
